"use client";

import { faMoon, faSun } from "@fortawesome/pro-solid-svg-icons";
import { useEffect, useState, useTransition } from "react";
import { Icon } from "@/components/ui/Icon";

type Theme = "light" | "dark";

const COOKIE = "jana-ui-theme";

function readCookie(): Theme {
	if (typeof document === "undefined") return "dark";
	const match = document.cookie.match(/(?:^|;\s*)jana-ui-theme=([^;]+)/);
	return match?.[1] === "light" ? "light" : "dark";
}

/**
 * Toggle the dashboard's UI theme. Writes a long-lived cookie and updates the
 * <html data-theme> attribute synchronously so the user sees an instant flip
 * without waiting for a server round-trip.
 */
export function ThemeToggle({
	initialTheme,
}: {
	/**
	 * The server-known theme (`getUiTheme()`), so the icon is right on the very
	 * first paint. Without it the toggle rendered its `"dark"` default for a
	 * frame on light-theme sessions before the cookie sync flipped it.
	 */
	initialTheme?: Theme;
} = {}) {
	const [theme, setTheme] = useState<Theme>(initialTheme ?? "dark");
	const [, startTransition] = useTransition();

	// Safety sync from the cookie on mount, for any render site that could not
	// thread the server-known theme (or a page rendered before the cookie was
	// applied).
	useEffect(() => {
		setTheme(readCookie());
	}, []);

	function flip() {
		const next: Theme = theme === "dark" ? "light" : "dark";
		setTheme(next);
		document.documentElement.setAttribute("data-theme", next);
		document.cookie = `${COOKIE}=${next}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
		// Re-render server components on the next interaction so freshly-rendered
		// SSR'd subtrees pick up the new cookie too.
		startTransition(() => {});
	}

	const isDark = theme === "dark";
	return (
		<button
			type="button"
			onClick={flip}
			aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
			title={isDark ? "Light mode" : "Dark mode"}
			className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border text-muted-foreground transition hover:bg-surface-2 hover:text-text"
		>
			<Icon icon={isDark ? faSun : faMoon} size={16} />
		</button>
	);
}
